import Table from "@components/table";
import React, { useEffect, useState } from "react";
import Typography from "@components/typography";
import styled from "@emotion/styled";
import { ThumbsUp, ThumbsDown, MessageCircle, StarIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import { ReviewApi } from "@apis/index";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Helper } from "@utils/index";

dayjs.extend(relativeTime);

// Styled Components
const Container = styled.div`
  background: #f8fafc;
  min-height: 100vh;
`;

const HeaderSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{
  type: "excellent" | "good" | "average" | "poor";
}>`
  background: white;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: ${(props) => {
      switch (props.type) {
        case "excellent":
          return "#10b981";
        case "good":
          return "#3b82f6";
        case "average":
          return "#f59e0b";
        case "poor":
          return "#ef4444";
        default:
          return "#e2e8f0";
      }
    }};
  }
`;

const StatIcon = styled.div<{
  type: "excellent" | "good" | "average" | "poor";
}>`
  width: 54px;
  height: 54px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    switch (props.type) {
      case "excellent":
        return "#f0fdf4";
      case "good":
        return "#eff6ff";
      case "average":
        return "#fffbeb";
      case "poor":
        return "#fef2f2";
      default:
        return "#f8fafc";
    }
  }};
  color: ${(props) => {
    switch (props.type) {
      case "excellent":
        return "#10b981";
      case "good":
        return "#3b82f6";
      case "average":
        return "#f59e0b";
      case "poor":
        return "#ef4444";
      default:
        return "#64748b";
    }
  }};
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 4px;
`;

const StatSubtext = styled.div<{
  type: "excellent" | "good" | "average" | "poor";
}>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => {
    switch (props.type) {
      case "excellent":
        return "#10b981";
      case "good":
        return "#3b82f6";
      case "average":
        return "#f59e0b";
      case "poor":
        return "#ef4444";
      default:
        return "#64748b";
    }
  }};
`;

const RatingDistribution = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #f1f5f9;
`;

const DistributionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RatingStars = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100px;
`;

const RatingBar = styled.div`
  flex: 1;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
`;

const RatingFill = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${(props) => props.width}%;
  background: ${(props) => props.color};
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const RatingCount = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 600;
  min-width: 40px;
  text-align: right;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const StarRating = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Star = styled.div<{ filled: boolean }>`
  color: ${(props) => (props.filled ? "#ffd700" : "#e2e8f0")};
`;

const ReviewCustomer = styled.div`
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
`;

const ReviewDate = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const ReviewComment = styled.div`
  color: #64748b;
  line-height: 1.5;
  max-width: 400px;
`;

const Reviews = () => {
  const { storeId: paramStoreId } = useParams();
  const user = Helper.getUser();
  // Admin views a specific store via the route param; a store user viewing
  // their own reviews has no param, so fall back to their own store id.
  const storeId =
    paramStoreId ||
    (user?.role === "store" || user?.role === "store-employee"
      ? user?.storeId || user?.id
      : undefined);
  const [reviews, setReviews] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        ReviewApi.list({ params: { storeId, limit: 50 } }),
        ReviewApi.stats({ params: { storeId } }),
      ]);
      setReviews(reviewsRes.items || []);
      setStats(statsRes);
    } catch (error: any) {
      toast.error(error.message || "Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (storeId) {
      fetchReviews();
    }
  }, [storeId]);

  const ratingColors = ["#10b981", "#3b82f6", "#f59e0b", "#f97316", "#ef4444"];

  const columns = [
    {
      title: "Customer",
      key: "customer",
      render: (record: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              background: "#fff7ed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#ff8c00",
              fontWeight: "600",
            }}
          >
            {record.customer?.name?.charAt(0) || "U"}
          </div>
          <div>
            <ReviewCustomer>
              {record.customer?.name || "Unknown User"}
            </ReviewCustomer>
            <ReviewDate>{dayjs(record.createdAt).fromNow()}</ReviewDate>
          </div>
        </div>
      ),
    },
    {
      title: "Rating",
      key: "rating",
      render: (record: any) => (
        <StarRating>
          {[1, 2, 3, 4, 5].map((star) => (
            <Star key={star} filled={star <= record.rating}>
              ⭐
            </Star>
          ))}
          <span
            style={{ marginLeft: "8px", fontWeight: "600", color: "#1e293b" }}
          >
            {record.rating}.0
          </span>
        </StarRating>
      ),
    },
    {
      title: "Comment",
      key: "comment",
      render: (record: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <ReviewComment>{record.comment || "No comment"}</ReviewComment>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
        Loading reviews...
      </div>
    );
  }

  return (
    <Container>
      <HeaderSection>
        <Typography weight={700} size={24}>
          Customer Reviews & Ratings
        </Typography>

        {/* Stats Grid */}
        <StatsGrid>
          <StatCard type="excellent">
            <StatIcon type="excellent">
              <StarIcon />
            </StatIcon>
            <StatContent>
              <StatValue>{stats?.averageRating || 0}/5</StatValue>
              <StatLabel>Average Rating</StatLabel>
              <StatSubtext type="excellent">
                {stats?.averageRating >= 4
                  ? "Excellent"
                  : stats?.averageRating >= 3
                    ? "Good"
                    : "Average"}
              </StatSubtext>
            </StatContent>
          </StatCard>

          <StatCard type="good">
            <StatIcon type="good">
              <ThumbsUp size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats?.totalReviews || 0}</StatValue>
              <StatLabel>Total Reviews</StatLabel>
              <StatSubtext type="good">
                Last update: {dayjs().format("MMM D")}
              </StatSubtext>
            </StatContent>
          </StatCard>

          <StatCard type="average">
            <StatIcon type="average">
              <MessageCircle size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats?.responseRate || 0}%</StatValue>
              <StatLabel>Response Rate</StatLabel>
              <StatSubtext type="average">Store engagement</StatSubtext>
            </StatContent>
          </StatCard>

          <StatCard type="poor">
            <StatIcon type="poor">
              <ThumbsDown size={24} />
            </StatIcon>
            <StatContent>
              <StatValue>{stats?.needsAttention || 0}</StatValue>
              <StatLabel>Needs Attention</StatLabel>
              <StatSubtext type="poor">Negative feedback</StatSubtext>
            </StatContent>
          </StatCard>
        </StatsGrid>

        {/* Rating Distribution */}
        <RatingDistribution>
          <Typography weight={600} size={18}>
            Rating Distribution
          </Typography>
          <br />
          {(stats?.distribution || []).map((rating: any, index: number) => (
            <DistributionItem key={rating.stars}>
              <RatingStars>
                <span
                  style={{ color: "#64748b", fontSize: "14px", width: "20px" }}
                >
                  {rating.stars}
                </span>
                <span>⭐</span>
                <span style={{ color: "#64748b", fontSize: "12px" }}>
                  ({rating.count})
                </span>
              </RatingStars>
              <RatingBar>
                <RatingFill
                  width={rating.percentage}
                  color={ratingColors[index]}
                />
              </RatingBar>
              <RatingCount>{rating.percentage}%</RatingCount>
            </DistributionItem>
          ))}
        </RatingDistribution>
      </HeaderSection>

      {/* Reviews Table */}
      <TableContainer>
        <Typography weight={600} size={18}>
          Recent Customer Reviews
        </Typography>
        <br />
        <Table columns={columns} data={reviews} />
      </TableContainer>
    </Container>
  );
};

export default Reviews;
